/**
 * A tiny string key/value store. In the browser it wraps `localStorage`, where every
 * call can throw (Safari private mode, quota, blocked storage, sandboxed iframes) — so
 * every access is wrapped and a failure simply behaves like "nothing stored".
 */
export interface KeyValueStore {
	get(key: string): string | null;
	/** Returns false when the write failed (quota, blocked storage). */
	set(key: string, value: string): boolean;
	remove(key: string): void;
	keys(): string[];
}

export function browserStore(): KeyValueStore {
	const storage = (): Storage | null => {
		try {
			return typeof window === 'undefined' ? null : window.localStorage;
		} catch {
			return null;
		}
	};
	return {
		get(key) {
			try {
				return storage()?.getItem(key) ?? null;
			} catch {
				return null;
			}
		},
		set(key, value) {
			try {
				const s = storage();
				if (!s) return false;
				s.setItem(key, value);
				return true;
			} catch {
				return false;
			}
		},
		remove(key) {
			try {
				storage()?.removeItem(key);
			} catch {
				// Nothing to do: storage is unavailable.
			}
		},
		keys() {
			try {
				const s = storage();
				if (!s) return [];
				const out: string[] = [];
				for (let i = 0; i < s.length; i++) {
					const k = s.key(i);
					if (k !== null) out.push(k);
				}
				return out;
			} catch {
				return [];
			}
		},
	};
}

/** In-memory store, for tests and as a stand-in when storage is unavailable. */
export function memoryStore(initial: Record<string, string> = {}): KeyValueStore {
	const map = new Map(Object.entries(initial));
	return {
		get: (key) => map.get(key) ?? null,
		set: (key, value) => {
			map.set(key, value);
			return true;
		},
		remove: (key) => void map.delete(key),
		keys: () => [...map.keys()],
	};
}
